import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Ticket, Lock } from "lucide-react";
import type { z } from "zod";

import {
  registerRequestSchema,
  type RegisterRequest,
  getCountryList,
  getDialPrefix,
} from "@sendsms/shared";
import { api } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import { meQueryKey } from "@/lib/auth/context";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
  CardFooter,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Checkbox } from "@/components/ui/Checkbox";
import { cn } from "@/lib/cn";

// Full ISO 3166-1 country list, built once at module load from the
// shared countries module (libphonenumber-js + Intl.DisplayNames).
// English names; the form is bilingual but country names in English
// are universally understood and avoid ~250 i18n keys per locale.
const COUNTRY_OPTIONS = getCountryList("en");

// Phone normalization uses getDialPrefix() from the shared countries
// module -- E.164 prefixes derived from libphonenumber-js, never
// hardcoded. registerRequestSchema uses the STRICT phoneSchema, which
// rejects national format like "0722..."; we promote it before submit.

/**
 * Promote a user-typed phone to E.164 digits-only.
 *  "0722000000" + RO   -> "40722000000"
 *  "0040722000000"     -> "40722000000"
 *  "+40 722 000 000"   -> "40722000000"
 *  "40722000000"       -> "40722000000" (unchanged)
 * Best-effort only; the schema still validates the result.
 */
function normalizePhone(raw: string, countryCode: string): string {
  let v = raw.trim().replace(/[\s\-()]/g, "");
  if (v.startsWith("+")) v = v.slice(1);
  if (v.startsWith("00")) return v.slice(2);
  if (v.startsWith("0")) {
    const prefix = getDialPrefix(countryCode) ?? "";
    return prefix + v.slice(1);
  }
  return v;
}

type RegisterFormValues = z.input<typeof registerRequestSchema>;

type Phase =
  | { step: "code" }
  | { step: "form"; code: string; trustedEmail: string };

export default function RegisterPage() {
  const [phase, setPhase] = useState<Phase>({ step: "code" });

  if (phase.step === "code") {
    return (
      <CodeGate
        onVerified={(code, trustedEmail) =>
          setPhase({ step: "form", code, trustedEmail })
        }
      />
    );
  }

  return (
    <RegisterForm
      code={phase.code}
      trustedEmail={phase.trustedEmail}
      onBack={() => setPhase({ step: "code" })}
    />
  );
}

// ---------------------------------------------------------------
// Phase 1 -- invite code gate
// ---------------------------------------------------------------

function CodeGate({
  onVerified,
}: {
  onVerified: (code: string, trustedEmail: string) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const verify = useMutation({
    mutationFn: (c: string) => api.verifyInvite({ code: c }),
    onSuccess: (res) => {
      if (res.valid && res.trusted_email) {
        onVerified(code.trim().toUpperCase(), res.trusted_email);
      } else {
        setError(t("auth.register.invite.invalid"));
      }
    },
    onError: (err: unknown) => {
      setError(translateApiError(t, err));
    },
  });

  const trimmed = code.trim();
  const canSubmit = trimmed.length > 0 && !verify.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.register.invite.title")}</CardTitle>
        <CardSubtitle>{t("auth.register.invite.subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        {error ? (
          <Alert tone="danger" description={error} className="mb-5" />
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (canSubmit) verify.mutate(trimmed);
          }}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.register.invite.code_label")}
            placeholder={t("auth.register.invite.code_placeholder")}
            autoFocus
            required
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (error) setError(null);
            }}
            hint={t("auth.register.invite.code_help")}
          />
          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={verify.isPending}
            disabled={!canSubmit}
          >
            {t("auth.register.invite.continue")}
          </Button>
        </form>
      </CardBody>

      <CardFooter>
        <p className="text-caption text-center text-ink-muted">
          {t("auth.register.have_account")}{" "}
          <Link
            to="/login"
            className="font-medium text-brand-500 hover:underline"
          >
            {t("auth.register.sign_in")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}

// ---------------------------------------------------------------
// Phase 2 -- the actual registration form
// ---------------------------------------------------------------

function RegisterForm({
  code,
  trustedEmail,
  onBack,
}: {
  code: string;
  trustedEmail: string;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showPassword, setShowPassword] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);

  const form = useForm<RegisterFormValues>({
    resolver: zodResolver(registerRequestSchema),
    defaultValues: {
      // Email is locked to the invite trusted email (V246).
      email: trustedEmail,
      password: "",
      full_name: "",
      organization_name: "",
      country_code: "RO",
      tax_id: "",
      phone: "",
      accepted_terms: false as unknown as true,
      code,
    },
    mode: "onSubmit",
  });

  const mutation = useMutation({
    mutationFn: (body: RegisterRequest) => api.register(body),
    onSuccess: async () => {
      navigate("/mfa/setup", { replace: true });
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
    onError: (err: unknown) => {
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  const err = (name: keyof RegisterFormValues): string | undefined => {
    const e = form.formState.errors[name];
    if (!e?.message) return undefined;
    return translateFieldError(t, String(e.message));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.register.title")}</CardTitle>
        <CardSubtitle>{t("auth.register.subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        {/* Invite context banner */}
        <div className="mb-5 flex items-center gap-2 rounded-md bg-surface-subtle px-3 py-2.5">
          <Ticket className="h-4 w-4 flex-shrink-0 text-brand-500" />
          <span className="text-caption text-ink-muted">
            {t("auth.register.invite.registering_for")}{" "}
            <span className="font-semibold text-ink">{trustedEmail}</span>
          </span>
          <button
            type="button"
            onClick={onBack}
            className="ml-auto text-caption font-medium text-brand-500 hover:underline"
          >
            {t("auth.register.invite.change_code")}
          </button>
        </div>

        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        <form
          onSubmit={form.handleSubmit((data) => {
            setTopError(null);
            const payload: RegisterRequest = {
              ...data,
              email: trustedEmail,
              code,
              tax_id: data.tax_id?.trim() || undefined,
              // PO principle: never ask what the browser already knows.
              // Sent silently; Settings offers explicit control (tz pas 2).
              tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
              phone: normalizePhone(
                String(data.phone ?? ""),
                String(data.country_code ?? "RO"),
              ),
            } as RegisterRequest;
            mutation.mutate(payload);
          })}
          className="space-y-4"
          noValidate
        >
          {/* Account section */}
          <div className="space-y-1 pb-1">
            <div className="text-micro font-semibold uppercase tracking-wider text-ink-subtle">
              {t("auth.register.step_account")}
            </div>
          </div>

          <Input
            label={t("auth.register.full_name_label")}
            placeholder={t("auth.register.full_name_placeholder")}
            autoComplete="name"
            autoFocus
            required
            {...form.register("full_name")}
            error={err("full_name")}
          />

          {/* Email is locked to the invite trusted email (V246). */}
          <Input
            label={t("auth.register.email_label")}
            type="email"
            value={trustedEmail}
            readOnly
            disabled
            hint={t("auth.register.invite.email_locked")}
            trailingElement={<Lock className="h-4 w-4 text-ink-subtle" />}
          />

          <Input
            label={t("auth.register.password_label")}
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            hint={t("auth.register.password_help")}
            {...form.register("password")}
            error={err("password")}
            trailingElement={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? t("common.hide") : t("common.show")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-muted focus:outline-none focus-visible:shadow-focus"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />

          {/* Organization section */}
          <div className="space-y-1 pb-1 pt-3">
            <div className="text-micro font-semibold uppercase tracking-wider text-ink-subtle">
              {t("auth.register.step_organization")}
            </div>
          </div>

          <Input
            label={t("auth.register.organization_name_label")}
            placeholder={t("auth.register.organization_name_placeholder")}
            autoComplete="organization"
            required
            {...form.register("organization_name")}
            error={err("organization_name")}
          />

          <CountrySelect
            label={t("auth.register.country_code_label")}
            required
            {...form.register("country_code")}
            error={err("country_code")}
          />

          <Input
            label={t("auth.register.tax_id_label")}
            placeholder={t("auth.register.tax_id_placeholder")}
            hint={t("auth.register.tax_id_help")}
            {...form.register("tax_id")}
            error={err("tax_id")}
          />

          <Input
            label={t("auth.register.phone_label")}
            type="tel"
            placeholder={t("auth.register.phone_placeholder")}
            autoComplete="tel"
            required
            hint={t("auth.register.phone_help")}
            {...form.register("phone", {
              // Normalize BEFORE validation (the strict phoneSchema sees the
              // promoted E.164 value, so national formats like 0722... pass
              // exactly as the field hint promises). Submit re-normalizes --
              // idempotent, kept as defense.
              setValueAs: (v) =>
                normalizePhone(
                  String(v ?? ""),
                  String(form.getValues("country_code") ?? "RO"),
                ),
            })}
            error={err("phone")}
          />

          {/* Terms */}
          <div className="pt-2">
            <Checkbox
              checked={Boolean(form.watch("accepted_terms"))}
              onCheckedChange={(checked) =>
                form.setValue(
                  "accepted_terms",
                  (checked === true) as unknown as true,
                  { shouldValidate: true },
                )
              }
              error={err("accepted_terms")}
              label={
                <>
                  {t("auth.register.accept_terms_prefix")}{" "}
                  <Link
                    to="/terms"
                    target="_blank"
                    className="text-brand-500 hover:underline"
                  >
                    {t("auth.register.accept_terms_link")}
                  </Link>{" "}
                  {t("common.and")}{" "}
                  <Link
                    to="/privacy"
                    target="_blank"
                    className="text-brand-500 hover:underline"
                  >
                    {t("auth.register.accept_terms_privacy_link")}
                  </Link>
                  .
                </>
              }
            />
          </div>

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={mutation.isPending}
          >
            {t("auth.register.submit")}
          </Button>
        </form>
      </CardBody>

      <CardFooter>
        <p className="text-caption text-center text-ink-muted">
          {t("auth.register.have_account")}{" "}
          <Link
            to="/login"
            className="font-medium text-brand-500 hover:underline"
          >
            {t("auth.register.sign_in")}
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}

const CountrySelect = ({
  label,
  error,
  required,
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement> & {
  label: string;
  error?: string;
}) => {
  const id = rest.id ?? `country-${rest.name}`;
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-caption font-medium text-ink">
        {label}
        {required ? (
          <span className="ml-0.5 text-danger-500" aria-hidden="true">*</span>
        ) : null}
      </label>
      <select
        {...rest}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        className={cn(
          "block h-11 w-full cursor-pointer appearance-none rounded-md bg-surface-subtle px-3.5 pr-9 text-body text-ink",
          "border border-transparent transition-colors",
          "focus:bg-surface-card focus:border-brand-500 focus:shadow-focus focus:outline-none",
          error && "border-danger-500",
        )}
      >
        {COUNTRY_OPTIONS.map((c) => (
          <option key={c.code} value={c.code}>
            {c.name} ({c.code})
          </option>
        ))}
      </select>
      {error ? (
        <p className="text-caption text-danger-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};
